import {
  PrimitiveType,
  SupportedPropertyType,
  TJsonSchemaDocument,
  TSchemaObject,
} from '@commenda/types';
import { v4 as uuidV4 } from 'uuid';
import { makeSchemaObject } from './domain';

export class JsonSchemaConverterHelper {
  /**
   * Recursively build a JSON Schema object from a TSchemaObject
   */
  static schemaObjectToJSONSchema(
    schemaObject: TSchemaObject
  ): Record<string, any> {
    const updateBase = (base: Record<string, any>) => {
      if (schemaObject.title) return { ...base, title: schemaObject.title };
      return base;
    };

    switch (schemaObject.type) {
      case SupportedPropertyType.STRING:
      case SupportedPropertyType.NUMBER:
      case SupportedPropertyType.BOOLEAN: {
        const base = {
          type: schemaObject.allowNull
            ? [schemaObject.type, 'null']
            : schemaObject.type,
        };
        return updateBase(base);
      }
      case SupportedPropertyType.ENUM: {
        const base = schemaObject.enumBase ?? PrimitiveType.STRING;
        const values = schemaObject.enumValues ?? [];
        if (schemaObject.allowNull) {
          // allow null without forcing it into enum array
          const result = {
            anyOf: [{ type: base, enum: values }, { type: 'null' }],
          };
          return updateBase(result);
        }
        const result = { type: base, enum: values };
        return updateBase(result);
      }
      case SupportedPropertyType.ARRAY: {
        const { items } = schemaObject;
        const type = schemaObject.allowNull ? ['array', 'null'] : 'array';
        if (!items) {
          const result = { type };
          return updateBase(result);
        }
        if (items.type === SupportedPropertyType.OBJECT) {
          const result = {
            type,
            items: this.objectSchemaFromChildren(
              items.nestedProperties ?? [],
              items.additionalProperties
            ),
          };
          return updateBase(result);
        }
        // primitive items
        const result = {
          type,
          items: { type: items.type },
        };
        return updateBase(result);
      }
      case SupportedPropertyType.OBJECT: {
        const schema = this.objectSchemaFromChildren(
          schemaObject.properties ?? [],
          schemaObject.additionalProperties
        );
        if (schemaObject.allowNull) {
          // valid JSON Schema to have multiple types while keeping object keywords
          schema.type = ['object', 'null'];
        }
        return updateBase(schema);
      }
      default:
        return {};
    }
  }

  /**
   * Build a JSON Schema object from an array of TJsonSchemaNode children
   */
  static objectSchemaFromChildren(
    childSchemaObjects: TSchemaObject[],
    additionalProps?: boolean
  ): Record<string, any> {
    const properties: Record<string, object> = {};
    const required: string[] = [];
    childSchemaObjects.forEach((childSchemaObject) => {
      properties[childSchemaObject.name] =
        this.schemaObjectToJSONSchema(childSchemaObject);
      if (childSchemaObject.required) required.push(childSchemaObject.name);
    });
    const base: Record<string, unknown> = {
      type: 'object',
      properties,
      additionalProperties: additionalProps ?? false,
    };
    if (required.length) base.required = required;
    return base;
  }

  /**
   * Build full JSON Schema doc (object at root)
   */
  static buildJSONSchema(doc: TJsonSchemaDocument): Record<string, any> {
    return this.schemaObjectToJSONSchema(doc.root);
  }

  /**
   * Importer (subset) JSON Schema -> TJsonSchemaDocument
   */
  static importFromJSONSchema(
    json: Record<string, any>,
    docName = 'Untitled'
  ): TJsonSchemaDocument {
    const root = makeSchemaObject('root', true);
    const { isNullable, baseType } = this.normalizeType(json?.type);
    root.allowNull = isNullable;
    root.additionalProperties = this.toAdditionalProperties(
      json?.additionalProperties
    );
    root.properties = this.importObjectProperties({
      ...json,
      type: baseType || 'object',
    });
    return { name: docName, root };
  }

  /**
   * Helper: Import object properties from JSON Schema
   */
  private static importObjectProperties(
    json: Record<string, any>
  ): TSchemaObject[] {
    if (!json || json.type !== 'object') return [];
    const props: TSchemaObject[] = [];
    const requiredSet = new Set<string>((json.required as string[]) || []);
    const properties =
      (json.properties as Record<string, Record<string, any>>) || {};
    Object.keys(properties).forEach((key) => {
      const schema = properties[key];
      const required = requiredSet.has(key);
      props.push(this.importSchemaObjectFromJsonSchema(key, schema, required));
    });
    return props;
  }

  /**
   * Helper: Import a single node from JSON Schema
   */
  private static importSchemaObjectFromJsonSchema(
    name: string,
    schema: Record<string, any>,
    required: boolean
  ): TSchemaObject {
    const title = schema?.title;

    if (!schema) {
      return {
        id: uuidV4(),
        name,
        type: SupportedPropertyType.STRING,
        required,
        ...(title && { title }),
      };
    }

    // handle enum-with-null via anyOf and via direct enum containing null
    if (schema.anyOf) {
      const hasNull = schema.anyOf.some((s: any) => s?.type === 'null');
      const other =
        schema.anyOf.find((s: any) => s?.type && s.type !== 'null') ?? {};
      const n = this.importSchemaObjectFromJsonSchema(name, other, required);
      n.allowNull = hasNull || n.allowNull;
      if (title) n.title = title;
      return n;
    }

    // normalize type possibly being array with "null"
    const { isNullable, baseType } = this.normalizeType(schema.type);

    if (schema.enum) {
      const first = schema.enum?.[0];
      let enumBase: PrimitiveType;
      if (typeof first === 'number') {
        enumBase = PrimitiveType.NUMBER;
      } else if (typeof first === 'boolean') {
        enumBase = PrimitiveType.BOOLEAN;
      } else {
        enumBase = PrimitiveType.STRING;
      }
      return {
        id: uuidV4(),
        name,
        type: SupportedPropertyType.ENUM,
        required,
        enumBase,
        enumValues: schema.enum as Array<string | number | boolean>,
        allowNull: isNullable,
        ...(title && { title }),
      };
    }

    switch (baseType) {
      case 'string':
      case 'number':
      case 'boolean':
        return {
          id: uuidV4(),
          name,
          type: baseType,
          required,
          allowNull: isNullable,
          ...(title && { title }),
        };

      case 'array': {
        const items = schema.items as Record<string, any> | undefined;
        if (
          items?.type === 'object' ||
          (Array.isArray(schema.items?.type) &&
            (schema.items?.type ?? []).includes('object'))
        ) {
          return {
            id: uuidV4(),
            name,
            type: SupportedPropertyType.ARRAY,
            required,
            allowNull: isNullable,
            items: {
              type: SupportedPropertyType.OBJECT,
              nestedProperties: this.importObjectProperties(items),
              additionalProperties: this.toAdditionalProperties(
                items?.additionalProperties
              ),
            },
            ...(title && { title }),
          };
        }
        const itemKind: SupportedPropertyType =
          (items?.type as SupportedPropertyType) ??
          SupportedPropertyType.STRING;
        return {
          id: uuidV4(),
          name,
          type: SupportedPropertyType.ARRAY,
          required,
          items: { type: itemKind },
          allowNull: isNullable,
          ...(title && { title }),
        };
      }

      case 'object': {
        const allowAdditionalProperties = this.toAdditionalProperties(
          schema.additionalProperties
        );
        return {
          id: uuidV4(),
          name,
          type: SupportedPropertyType.OBJECT,
          required,
          properties: this.importObjectProperties(schema),
          additionalProperties: allowAdditionalProperties,
          ...(title && { title }),
        };
      }

      default:
        return {
          id: uuidV4(),
          name,
          type: SupportedPropertyType.STRING,
          required,
          allowNull: isNullable,
          ...(title && { title }),
        };
    }
  }

  private static normalizeType(t: any): {
    isNullable: boolean;
    baseType: SupportedPropertyType | undefined;
  } {
    if (Array.isArray(t)) {
      const isNullable = t.includes('null');
      const base = (t.find((x) => x !== 'null') ?? undefined) as any;
      return { isNullable, baseType: base };
    }
    return { isNullable: false, baseType: t };
  }

  private static toAdditionalProperties(v: any): boolean | undefined {
    if (typeof v === 'boolean') return v;
    if (typeof v === 'string') return v === 'true';
    return false;
  }
}
